import { Box, Stack, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { API_BASE_URL } from '@synergy/config';
import { AccountContactInfo } from '../../api/account-api';
import { Api } from '../../../api';
import { Contact } from '../../models/contact';
import { PaloLockedBadge } from './palo-locked-badge';
import { ROUTES } from '../../../features/sidebar-menu/navigation-drawer/routes';
import { contactPhones, paloTelHref } from './palo-contact-reach';
import { fetchContactInfo } from '../../actions/contact-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const c = tokens.color;

/** A person as a list row carries them — the embedded copy, not the master record. */
export interface PaloCardPerson {
    id?: number | null;
    fullName?: string | null;
    email?: string | null;
    phone?: string | null;
}

/** A company as a list row carries it, with its main contact. */
export interface PaloCardCompany {
    id?: number | null;
    name?: string | null;
    /** Locked on the master record today — the list says so (LST-S14). */
    isLocked?: boolean | null;
    mainPhone?: string | null;
    mainEmail?: string | null;
    mainContact?: PaloCardPerson | null;
}

/** Which master record the "open" link leads to. */
export type PaloCardKind = 'supplier' | 'customer';

const Label = ({ children }: { children: ReactNode }): ReactElement => (
    <Box sx={{ fontSize: '10px', color: c.ink3, letterSpacing: '.04em', textTransform: 'uppercase' }}>{children}</Box>
);

const Reach = ({ label, value, href }: { label: ReactNode; value: string; href?: string }): ReactElement => (
    <Box sx={{ fontSize: '12px', lineHeight: 1.4 }}>
        <Label>{label}</Label>
        {href ? (
            <Box component="a" href={href} sx={{ color: c.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {value}
            </Box>
        ) : (
            // Not every number is one: some entries are notes ("neue Firma ab
            // 2001, Tel. stimmt noch."), and a call link built from those
            // dials nonsense. They are shown, not linked (#2229).
            <Box component="span" sx={{ color: c.ink }}>
                {value}
            </Box>
        )}
    </Box>
);

const OpenLink = ({ to, children }: { to: string; children: ReactNode }): ReactElement => (
    <Box
        component={Link}
        to={to}
        sx={{
            fontSize: '11.5px',
            color: c.accent,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
        }}
    >
        {children}
    </Box>
);

const PersonCard = ({ person }: { person: PaloCardPerson }): ReactElement => {
    const dispatch = useThunkDispatch();
    const loaded = useAppSelector(state => (person.id ? state.contacts.contactDetails[person.id] : undefined));

    // The card is built when it opens, so this asks the server then and only
    // then — the row holds one number, the master record holds the mobile and
    // the rest (#2229).
    useEffect(() => {
        if (person.id && undefined === loaded) {
            dispatch(fetchContactInfo(person.id));
        }
        // Once per person: a card reopened after the answer reads it from the store.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [person.id]);

    const detail: Contact | null = loaded ?? null;
    const phones = contactPhones(detail, person.phone);
    const emails = [detail?.email ?? person.email, ...(detail?.emails ?? []).map(entry => entry.email)].filter(
        (value, index, all): value is string => !!value && all.indexOf(value) === index
    );

    return (
        <Stack gap="7px">
            <Box sx={{ fontSize: '12.5px', fontWeight: 620, color: c.ink }}>{person.fullName}</Box>
            {phones.map(phone => (
                <Reach
                    key={phone.value}
                    label={<FormattedMessage id={phone.labelId} />}
                    value={phone.value}
                    href={paloTelHref(phone.value)}
                />
            ))}
            {emails.map(email => (
                <Reach
                    key={email}
                    label={<FormattedMessage id="app.label.email" />}
                    value={email}
                    href={`mailto:${email}`}
                />
            ))}
            {0 === phones.length && 0 === emails.length && (
                <Box sx={{ fontSize: '12px', color: c.ink3 }}>
                    <FormattedMessage id="app.contactCard.noReach" />
                </Box>
            )}
            {person.id && (
                <OpenLink to={ROUTES.contactsAndOrganizations.contacts.detail.replace(':id', String(person.id))}>
                    <FormattedMessage id="app.contactCard.openContact" />
                </OpenLink>
            )}
        </Stack>
    );
};

/**
 * What a company's card showed before, kept for the next time it is opened.
 * A list row carries the name and little else, so the card asks the server —
 * once per company, and only when someone opens it (LST-S13).
 */
const reached = new Map<number, AccountContactInfo>();

const CompanyCard = ({ company, kind }: { company: PaloCardCompany; kind: PaloCardKind }): ReactElement => {
    const complete = !!company.mainPhone || !!company.mainEmail;
    const [loaded, setLoaded] = useState<AccountContactInfo | undefined>(
        company.id ? reached.get(company.id) : undefined
    );

    useEffect(() => {
        const id = company.id;

        if (!id || complete || reached.has(id)) {
            return undefined;
        }

        const abort = new AbortController();

        void new Api(API_BASE_URL).accounts.getContactInfo(id, abort.signal).then(answer => {
            if (answer.success && answer.result) {
                reached.set(id, answer.result);
                setLoaded(answer.result);
            }
        });

        return () => abort.abort();
    }, [company.id, complete]);

    const mainPhone = company.mainPhone ?? loaded?.mainPhone;
    const mainEmail = company.mainEmail ?? loaded?.mainEmail;
    const contact: PaloCardPerson | null | undefined = company.mainContact ?? (loaded?.mainContactName
        ? {
              id: loaded.mainContactId,
              fullName: loaded.mainContactName,
              phone: loaded.mainContactPhone,
              email: loaded.mainContactEmail,
          }
        : undefined);
    const route =
        'supplier' === kind
            ? ROUTES.contactsAndOrganizations.suppliers.detail
            : ROUTES.contactsAndOrganizations.customers.detail;

    return (
        <Stack gap="7px">
            <Box sx={{ fontSize: '12.5px', fontWeight: 620, color: c.ink }}>{company.name}</Box>
            {mainPhone && (
                <Reach
                    label={<FormattedMessage id="app.contactCard.switchboard" />}
                    value={mainPhone}
                    href={paloTelHref(mainPhone)}
                />
            )}
            {mainEmail && (
                <Reach
                    label={<FormattedMessage id="app.label.email" />}
                    value={mainEmail}
                    href={`mailto:${mainEmail}`}
                />
            )}
            {contact?.fullName && (
                <Box sx={{ borderTop: `1px solid ${c.lineSoft}`, paddingTop: '6px' }}>
                    <Label>
                        <FormattedMessage id="app.contactCard.mainContact" />
                    </Label>
                    <Box sx={{ fontSize: '12px', color: c.ink, marginBottom: '4px' }}>{contact.fullName}</Box>
                    {contact.phone && (
                        <Reach
                            label={<FormattedMessage id="app.contacts.phone.default" />}
                            value={contact.phone}
                            href={paloTelHref(contact.phone)}
                        />
                    )}
                    {contact.email && (
                        <Reach
                            label={<FormattedMessage id="app.label.email" />}
                            value={contact.email}
                            href={`mailto:${contact.email}`}
                        />
                    )}
                </Box>
            )}
            {!mainPhone && !mainEmail && !contact?.fullName && (
                <Box sx={{ fontSize: '12px', color: c.ink3 }}>
                    <FormattedMessage id="app.contactCard.noReach" />
                </Box>
            )}
            {company.id && (
                <OpenLink to={route.replace(':id', String(company.id))}>
                    <FormattedMessage id={'supplier' === kind ? 'app.contactCard.openSupplier' : 'app.contactCard.openCustomer'} />
                </OpenLink>
            )}
        </Stack>
    );
};

export interface PaloContactCardProps {
    person?: PaloCardPerson | null;
    company?: PaloCardCompany | null;
    kind?: PaloCardKind;
}

/**
 * The contact card behind a name in a list: phone, mobile and e-mail as links,
 * and the way to the master record.
 *
 * "Damit Mitarbeiter direkt aus der Liste anrufen oder mailen können" (GF
 * 07.10.2026, LST-S13) — until now the card hung on the contact field of the
 * document mask alone, so reaching someone meant opening the document first.
 *
 * The card shows what the row already carries and asks the server only when it
 * is opened, and only for a person, whose mobile number the row does not hold.
 */
export const PaloContactCard = ({ person, company, kind = 'customer' }: PaloContactCardProps): ReactElement | null => {
    if (company) {
        return <CompanyCard company={company} kind={kind} />;
    }

    return person ? <PersonCard person={person} /> : null;
};

export interface PaloContactNameProps extends PaloContactCardProps {
    /** What the cell shows; the name by default. */
    children?: ReactNode;
}

/**
 * A name in a list, with its contact card on hover.
 *
 * The click stays with the row — the card is in addition, never instead
 * (LST-S13). It waits half a second, so running the mouse down a column does
 * not open one card after another.
 */
export const PaloContactName = ({ person, company, kind, children }: PaloContactNameProps): ReactElement => {
    const name = children ?? company?.name ?? person?.fullName ?? '';
    /*
     * A locked partner stays in the documents that were written with them —
     * what the list has to say is that nobody may work with them any more.
     * The pill is the one the master data lists already use; no second lock
     * symbol beside it (LST-S14, GF 12.10.2026).
     */
    const label = company?.isLocked ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%' }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {name}
            </Box>
            <PaloLockedBadge locked={true} />
        </Box>
    ) : (
        name
    );

    if (!person && !company) {
        return <>{label}</>;
    }

    return (
        <Tooltip
            enterDelay={500}
            enterNextDelay={500}
            placement="bottom-start"
            slotProps={{
                tooltip: {
                    sx: {
                        backgroundColor: c.surface,
                        color: c.ink,
                        border: `1px solid ${c.line}`,
                        borderRadius: tokens.radius.default + 'px',
                        boxShadow: tokens.shadow.s2,
                        padding: '10px 12px',
                        maxWidth: 320,
                    },
                },
            }}
            title={<PaloContactCard person={person} company={company} kind={kind} />}
        >
            <Box component="span" sx={{ display: 'inline-block', maxWidth: '100%' }}>
                {label}
            </Box>
        </Tooltip>
    );
};
